import { Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { TranscriptSegment } from '../../../sic-lib/types/Ai';
import { formatSecondsToMinutes } from '../../../sic-lib/utils/formatSecondsToMinutes';

interface Props {
    transcriptSegments: string | undefined;
    audioRef: React.RefObject<HTMLAudioElement>;
}

export default function TranscriptTab({ transcriptSegments, audioRef }: Props) {
    const { classes } = useStyles();

    const [currentTime, setCurrentTime] = React.useState<number>(0);

    const segments = React.useMemo(() => {
        if (!transcriptSegments) {
            return [];
        }
        return JSON.parse(transcriptSegments) as TranscriptSegment[];
    }, [transcriptSegments]);

    const handleSeek = React.useCallback(
        (seconds: number) => {
            if (audioRef.current) {
                audioRef.current.currentTime = seconds;
                audioRef.current.play();
            }
        },
        [audioRef],
    );

    // Update the current time when the audio element updates
    React.useEffect(() => {
        const audioElement = audioRef.current;
        if (!audioElement) return; // early return if no audio element
        setCurrentTime(audioElement.currentTime); //set the current time immediately
        const handleTimeUpdate = () => setCurrentTime(audioElement.currentTime); //setup updater
        audioElement.addEventListener('timeupdate', handleTimeUpdate); //register updater
        return () => {
            audioElement.removeEventListener('timeupdate', handleTimeUpdate); //cleanup
        };
    }, [audioRef]);

    return (
        <>
            <Typography variant="h6" mb={2}>
                Call Transcript
            </Typography>
            <div className={classes.table}>
                {segments.map(segment => (
                    <div
                        className={
                            currentTime > segment.Start && currentTime < segment.End
                                ? classes.segmentHighlighted
                                : classes.segment
                        }
                        key={segment.Id}
                        onClick={() => handleSeek(segment.Start)}
                    >
                        <div className={classes.timeStampWrapper}>
                            <Typography fontSize="0.75rem">
                                {formatSecondsToMinutes(segment.Start)}
                            </Typography>
                        </div>
                        <div className={classes.text}>
                            <Typography>{segment.Text}</Typography>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    table: {
        display: 'table',
        width: '100%',
        border: 'none',
    },
    segment: {
        display: 'table-row',
        padding: theme.spacing(0, 4),
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    segmentHighlighted: {
        display: 'table-row',
        padding: theme.spacing(0, 4),
        cursor: 'pointer',
        backgroundColor: theme.palette.action.focus,
    },
    timeStampWrapper: {
        display: 'table-cell',
        textAlign: 'right',
        color: theme.palette.text.disabled,
        padding: theme.spacing(0, 2),
        '& MuiTypography-body1': {
            fontSize: '0.4rem',
        },
    },
    text: {
        display: 'table-cell',
    },
}));
